import { request } from "../http/api-client";
import { getWallets } from "./payouts";
import { getTransactionStats, fetchTransactions } from "./transactions";
import type { DailyTransactionData } from "./admin-dashboard";

export interface MerchantInfo {
  id: string;
  name: string;
  status: string;
  kycVerified: boolean;
}

export interface WalletBalances {
  availableBalance: number;
  reservedBalance: number;
  totalBalance: number;
  currency: string;
}

export interface FeeStructure {
  percentage: number;
  fixed: number;
}

export interface FeeConfiguration {
  payin: FeeStructure;
  payout: FeeStructure;
}

export interface TransactionStats {
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  totalVolume: number;
  averageTransactionValue: number;
}

export interface DailyActivity {
  date: string;
  payinCount: number;
  payoutCount: number;
  payinVolume: number;
  payoutVolume: number;
}

export interface MonthlyTrend {
  month: string;
  transactions: number;
  volume: number;
  successRate: number;
}

export interface MerchantDashboardData {
  merchant: MerchantInfo;
  wallet: WalletBalances;
  feeConfiguration: FeeConfiguration;
  statistics: {
    transactionStats: TransactionStats;
    recentActivity: DailyActivity[];
    monthlyTrends: MonthlyTrend[];
  };
}

export interface VolumeChartPoint {
  date: string;
  payin: number;
  payout: number;
}

export interface StatsPeriodData {
  payinAmount: number;
  payinCount: number;
  payoutAmount: number;
  payoutCount: number;
  successRate: number;
  period: string;
  totalVolume?: number;
  totalPayinSuccess?: number;
  totalPayoutSuccess?: number;
}

/**
 * Fetch merchant dashboard data from real API
 */
export async function fetchMerchantDashboardData(): Promise<MerchantDashboardData> {
  try {
    const response = await request('/api/v1/merchants/dashboard', {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch dashboard data');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching merchant dashboard:', error);
    // Return default data on error
    return {
      merchant: {
        id: '',
        name: '',
        status: 'INACTIVE',
        kycVerified: false,
      },
      wallet: {
        availableBalance: 0,
        reservedBalance: 0,
        totalBalance: 0,
        currency: 'INR',
      },
      feeConfiguration: {
        payin: { percentage: 0, fixed: 0 },
        payout: { percentage: 0, fixed: 0 },
      },
      statistics: {
        transactionStats: {
          totalTransactions: 0,
          successfulTransactions: 0,
          failedTransactions: 0,
          totalVolume: 0,
          averageTransactionValue: 0,
        },
        recentActivity: [],
        monthlyTrends: [],
      },
    };
  }
}

/**
 * Fetch merchant statistics for different periods
 */
export async function fetchMerchantStats(period: 'today' | 'yesterday' | 'week' | 'month' | '90days'): Promise<StatsPeriodData> {
  try {
    const response = await request(`/api/v1/merchants/stats?period=${period}`, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch stats');
    }

    // The actual API returns data nested under the period name (e.g., "today")
    const periodData = response.data[period] || response.data;

    return {
      payinAmount: periodData.totalPayinSuccess || 0,
      payinCount: periodData.payinCount || periodData.totalCount || 0,
      payoutAmount: periodData.totalPayoutSuccess || 0,
      payoutCount: periodData.payoutCount || 0,
      successRate: periodData.successRate || 0,
      period,
      totalVolume: periodData.totalVolume || 0,
      totalPayinSuccess: periodData.totalPayinSuccess || 0,
      totalPayoutSuccess: periodData.totalPayoutSuccess || 0,
    };
  } catch (error) {
    console.error(`Error fetching merchant stats for ${period}:`, error);
    // Return default data on error
    return {
      payinAmount: 0,
      payinCount: 0,
      payoutAmount: 0,
      payoutCount: 0,
      successRate: 0,
      period,
    };
  }
}

/**
 * Get today's and yesterday's stats with change percentages
 */
export async function fetchMerchantTodayStats() {
  try {
    const [todayData, yesterdayData] = await Promise.all([
      fetchMerchantStats('today'),
      fetchMerchantStats('yesterday'),
    ]);

    // Calculate percentage changes
    const payinChange = yesterdayData.payinAmount > 0
      ? ((todayData.payinAmount - yesterdayData.payinAmount) / yesterdayData.payinAmount) * 100
      : 0;

    const payoutChange = yesterdayData.payoutAmount > 0
      ? ((todayData.payoutAmount - yesterdayData.payoutAmount) / yesterdayData.payoutAmount) * 100
      : 0;

    const successRateChange = yesterdayData.successRate > 0
      ? todayData.successRate - yesterdayData.successRate
      : 0;

    return {
      todayPayin: {
        amount: todayData.payinAmount,
        count: todayData.payinCount,
        change: parseFloat(payinChange.toFixed(1)),
      },
      todayPayout: {
        amount: todayData.payoutAmount,
        count: todayData.payoutCount,
        change: parseFloat(payoutChange.toFixed(1)),
      },
      successRate: {
        percentage: todayData.successRate,
        dailyChange: parseFloat(successRateChange.toFixed(1)),
      },
      totalVolume: todayData.totalVolume, // Add totalVolume from today's data
    };
  } catch (error) {
    console.error('Error fetching today stats:', error);
    return {
      todayPayin: { amount: 0, count: 0, change: 0 },
      todayPayout: { amount: 0, count: 0, change: 0 },
      successRate: { percentage: 0, dailyChange: 0 },
      totalVolume: 0,
    };
  }
}

/**
 * Convert recent activity to chart format
 */
export function formatRecentActivityForChart(recentActivity: DailyActivity[]): VolumeChartPoint[] {
  return recentActivity.map(activity => ({
    date: new Date(activity.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    payin: activity.payinVolume,
    payout: activity.payoutVolume,
  }));
}

/**
 * Calculate success rate percentage
 */
export function calculateSuccessRate(stats: TransactionStats): number {
  if (stats.totalTransactions === 0) return 0;
  return (stats.successfulTransactions / stats.totalTransactions) * 100;
}